import { useState, useEffect, useCallback } from 'react';

import { applyConsent } from '../utils/cookieManager';

const CONSENT_KEY = 'opushr_cookie_consent';
const CONSENT_VERSION = '2.1'; // Matches privacy policy version
const CONSENT_DURATION = 365 * 24 * 60 * 60 * 1000; // 12 months in milliseconds

const DEFAULT_CONSENT = {
  necessary: true, // Always true, cannot be disabled
  analytics: false, // Google Analytics, Hotjar
  marketing: false, // Meta Pixel (Facebook), Google Ads
  timestamp: null,
  version: CONSENT_VERSION,
};

export const useCookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [consent, setConsent] = useState(DEFAULT_CONSENT);
  const [hasInteracted, setHasInteracted] = useState(false);

  // Listen for consent changes from other components
  useEffect(() => {
    const handleConsentChange = (event) => {
      // Update local state when the event is fired by another component
      setConsent(event.detail);
      setHasInteracted(true);
      setShowBanner(false);
    };

    window.addEventListener('cookieConsentChange', handleConsentChange);

    return () => {
      window.removeEventListener('cookieConsentChange', handleConsentChange);
    };
  }, []);

  // Load consent from localStorage
  useEffect(() => {
    const loadConsent = () => {
      try {
        const stored = localStorage.getItem(CONSENT_KEY);

        if (!stored) {
          // No consent stored - show banner
          setShowBanner(true);
          return;
        }

        const parsed = JSON.parse(stored);

        // Check if consent is still valid (not expired and same version)
        if (
          parsed.version !== CONSENT_VERSION ||
          !parsed.timestamp ||
          Date.now() - parsed.timestamp > CONSENT_DURATION
        ) {
          // Expired or old version - show banner again
          setShowBanner(true);
          return;
        }

        // Valid consent found
        setConsent(parsed);
        setHasInteracted(true);
        setShowBanner(false);
      } catch (error) {
        console.error('Error loading cookie consent:', error);
        setShowBanner(true);
      }
    };

    loadConsent();
  }, []);

  // Apply consent whenever it changes
  useEffect(() => {
    if (hasInteracted) {
      applyConsent(consent);
    }
  }, [consent, hasInteracted]);

  // Save consent to localStorage
  const saveConsent = useCallback((newConsent) => {
    const consentToSave = {
      ...newConsent,
      necessary: true, // Always true
      timestamp: Date.now(),
      version: CONSENT_VERSION,
    };

    try {
      localStorage.setItem(CONSENT_KEY, JSON.stringify(consentToSave));
      setConsent(consentToSave);
      setHasInteracted(true);
      setShowBanner(false);
      setShowSettings(false);

      // Dispatch custom event for other components to react
      window.dispatchEvent(
        new CustomEvent('cookieConsentChange', { detail: consentToSave }),
      );
    } catch (error) {
      console.error('Error saving cookie consent:', error);
    }
  }, []);

  // Accept all cookies
  const acceptAll = useCallback(() => {
    saveConsent({
      necessary: true,
      analytics: true,
      marketing: true,
    });
  }, [saveConsent]);

  // Reject optional cookies (keep only necessary)
  const rejectAll = useCallback(() => {
    saveConsent({
      necessary: true,
      analytics: false,
      marketing: false,
    });
  }, [saveConsent]);

  // Accept a specific category and save immediately (atomic operation)
  const acceptCategory = useCallback((category) => {
    const newConsent = {
      ...consent,
      [category]: true,
      necessary: true,
      timestamp: Date.now(),
      version: CONSENT_VERSION,
    };

    try {
      localStorage.setItem(CONSENT_KEY, JSON.stringify(newConsent));
      setConsent(newConsent);
      setHasInteracted(true);

      // Dispatch event for other components
      window.dispatchEvent(
        new CustomEvent('cookieConsentChange', { detail: newConsent }),
      );
    } catch (error) {
      console.error('Error saving cookie consent:', error);
    }
  }, [consent]);

  // Update specific consent
  const updateConsent = useCallback(
    (category, value) => {
      if (category === 'necessary') {
        // Cannot disable necessary cookies
        return;
      }

      const newConsent = {
        ...consent,
        [category]: value,
      };

      setConsent(newConsent);
    },
    [consent],
  );

  // Save custom settings
  const saveSettings = useCallback(() => {
    // Get previous consent to detect what changed
    const previousConsent = JSON.parse(
      localStorage.getItem(CONSENT_KEY) || '{}',
    );

    // Check if user disabled any categories (need reload to clean up)
    const needsReload =
      (previousConsent.marketing && !consent.marketing) ||
      (previousConsent.analytics && !consent.analytics);

    saveConsent(consent);

    // Reload page if user disabled categories to properly clean up scripts/cookies
    if (needsReload) {
      setTimeout(() => {
        window.location.reload();
      }, 100);
    }
  }, [consent, saveConsent]);

  // Open settings modal
  const openSettings = useCallback(() => {
    setShowSettings(true);
  }, []);

  // Close settings modal
  const closeSettings = useCallback(() => {
    setShowSettings(false);
  }, []);

  // Reset consent (for testing or user request)
  const resetConsent = useCallback(() => {
    localStorage.removeItem(CONSENT_KEY);
    setConsent(DEFAULT_CONSENT);
    setHasInteracted(false);
    setShowBanner(true);
    setShowSettings(false);
  }, []);

  return {
    // State
    showBanner,
    showSettings,
    consent,
    hasInteracted,

    // Actions
    acceptAll,
    rejectAll,
    acceptCategory,
    updateConsent,
    saveSettings,
    openSettings,
    closeSettings,
    resetConsent,
  };
};
